import React, { PureComponent } from 'react';
import { Loader } from 'react-loaders';
import styled from 'styled-components';
//import moment from "moment-timezone";
import orderBy from 'lodash/orderBy';

import { changeLocation } from '../../api/Location';
//import { isSurveyFinished } from '../../api/Surveys';
import { getRequest } from '../../api/ApiService';
import { fetchTracks } from '../../services/tracks-cache';
import Auth from '../../routes/Auth';
import { resolveOnboardingEntry } from '../../instructor/components/onboarding/onboardingRouting';

const Wrapper = styled.div`
  .loader {
    display: inline-block;
    margin-top: 20px;
    margin-left: 20px;
  }

  .loader .loader-inner div {
    border-color: #36507e;
  }
`;

class StartPage extends PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      loaded: false,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps) {
    // The IAS / Co-Instructor branch of fetchData reads this.props.rounds
    // synchronously and uses it to pick between /instructor/welcome and
    // /instructor/performance-feedback. On first mount, App.js's
    // general-info pipeline (minimal response, then refetch with game_id)
    // may not have resolved yet, so rounds is [] and the branch ships
    // the IAS to /instructor/welcome?round=1 even when the course has
    // finished rounds — see continueInstructorRouting below.
    //
    // Watch for props.game to land an id (set alongside rounds inside
    // App.js's general-info success handler). When it arrives, retry
    // fetchData so the branch sees the populated rounds. One-shot
    // _routed guard inside fetchData prevents re-routing after the
    // instructor has already been sent somewhere.
    if (
      this._routed
      || !(Auth.isCoInstructor() || Auth.isInstructorAssistant())
    ) return;
    const prevGameId = prevProps.game && prevProps.game.id;
    const nextGameId = this.props.game && this.props.game.id;
    if (nextGameId && !prevGameId) {
      this.fetchData();
    }
  }

  fetchData = () => {
    if (Auth.isCoInstructor() || Auth.isInstructorAssistant()) {
      // Only the course-lead Instructor (INS) owns onboarding —
      // Co-Instructors and Instructor Assistants can't create games
      // or surveys, so skip the resolveOnboardingEntry gate and go
      // straight to the normal landing (welcome / performance-feedback).
      //
      // Routing reads this.props.rounds, which is only populated after
      // App.js's general-info call resolves. Bail out when props.game
      // doesn't have an id yet — that's the signal that general-info
      // is still in flight. componentDidUpdate fires fetchData again
      // once game.id lands. The _routed one-shot keeps a later
      // componentDidUpdate (e.g. props.game being reassigned on a
      // game-switch) from navigating the instructor a second time.
      const game = this.props.game;
      if (!game || !game.id) return;
      this._routed = true;
      this.continueInstructorRouting();
      return;
    }
    if (Auth.getPosition() === 'Instructor') {
      // Async gate: resolve full vs partial onboarding vs. skip based on
      // user.onboard + whether the current course already has a game
      // and a survey. See onboardingRouting.js for the rules.
      resolveOnboardingEntry()
        .then(onboardingUrl => {
          if (onboardingUrl) {
            changeLocation(onboardingUrl);
            return;
          }
          this.continueInstructorRouting();
        })
        .catch(() => {
          // If the check itself fails, fall through to the existing
          // welcome routing rather than blocking the user.
          this.continueInstructorRouting();
        });
      return;
    } else {
      // Intro gate safety net for reloads. If a student refreshes /start
      // before watching the intro video, bounce them back to it. Login
      // itself also routes here first; this covers the reload case.
      const currentUser = Auth.getUser();
      if (currentUser && currentUser.intro === 0) {
        changeLocation('/intro-video');
        return;
      }

      if (Auth.isPaid() === false) {
        this.setState({
          loaded: true,
        });
        return;
      }

      getRequest(`${process.env.REACT_APP_API_URL}game/rounds/`)
        .then(res => {
          const allRounds = orderBy(res.body, ['number'], ['asc']);
          const startedRounds = allRounds.filter(i => i.is_started === true);

          if (startedRounds.length === 0) {
            changeLocation('/onboarding');
            return;
          }

          const currentRound = startedRounds[startedRounds.length - 1];
          if (currentRound.is_finished === true || currentRound.should_be_finished === true) {
            changeLocation(`/performance-feedback?round=${currentRound.number}`);
            return;
          }

          if (currentRound.number === 1) {
            changeLocation(`/onboarding?round=${currentRound.number}`);
            return;
          }

          const courseId = Number(Auth.getCourseId());
          const gameId = Number(Auth.getGameId());
          const userId = Number(Auth.getUserId());
          const priorRound = currentRound.number - 1;

          fetchTracks({ gameId, courseId, userId, roundNumber: priorRound })
            .then(res => {
              const visited = res.body.results
                .find(i => (
                  i.page === `${window.location.protocol}//${window.location.host}/performance-feedback/good-luck`
                ));

              if (!visited) {
                changeLocation(`/performance-feedback?round=${priorRound}`);
                window.location.reload();
              } else {
                changeLocation(`/onboarding?round=${currentRound.number}`);
              }
            })
            .catch(err => { });
        })
        .catch(err => { });
    }
  }

  // Existing instructor welcome/performance-feedback routing, extracted
  // from fetchData so the new async onboarding gate can fall through
  // to it when the instructor doesn't need onboarding.
  continueInstructorRouting = () => {
    const { rounds } = this.props;
    // Sort by round number ascending before taking the last element.
    // The BE returns rounds in insertion / id order, not number order,
    // so after Activate Main Rounds the array can land as (2, 3, 1)
    // and `finishedRounds[last]` would be Round 1 instead of the real
    // latest round. See also the twin fix on performance-feedback.js.
    const finishedRounds = (rounds || [])
      .filter(i => i.is_started === true && i.is_finished === true)
      .slice()
      .sort((a, b) => Number(a.number) - Number(b.number));
    if (finishedRounds.length === 0) {
      changeLocation('/instructor/welcome?round=1');
    } else {
      const lastFinishedRound = finishedRounds[finishedRounds.length - 1];
      changeLocation(`/instructor/performance-feedback?round=${lastFinishedRound.number}`);
    }
  };

  render() {
    const { loaded } = this.state;

    return !loaded ? (
      <Wrapper>
        <Loader type="ball-scale-ripple" active />
      </Wrapper>
    ) : <Wrapper />;
  }
}

export default StartPage;



// WEBPACK FOOTER //
// ./src/components/navigation/start.js